import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import {
  PanelLeftClose, PanelLeftOpen, Settings, LogOut, User as UserIcon,
  Sun, Moon, LayoutDashboard, Gauge, Target,
  FileText, Building2, ClipboardList, CalendarClock, Clock3,
  ShoppingCart, Wrench, Map as MapIcon, ChevronDown, ChevronRight, Receipt,
} from "lucide-react";
import { useAuth } from "@/auth/AuthContext";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { resolveInitialTheme, toggleTheme as toggleThemeUtil, subscribeTheme } from "@/lib/theme";
import { resolveLandingPath } from "@/lib/landingPages";
import { ConnectOutlookButton } from "@/components/common/ConnectOutlookButton";
import { UserPreferences } from "@/components/common/UserPreferences";
import { useI18n } from "@/i18n";

// Visibility is decided ONLY by the access matrix (Settings → Users →
// Permissions) via each item's `surface`. `key` mirrors the old testid
// slug so `nav-<key>` selectors keep resolving alongside `nav-tab-<key>`.
export const NAV_GROUPS = [
  {
    label: "Sales",
    items: [
      { key: "sales-dashboard", path: "/sales", label: "Sales Dashboard", icon: LayoutDashboard, surface: "sales.dashboard" },
      { key: "leads", path: "/leads", label: "Leads", icon: Target, surface: "sales.leads" },
      { key: "proposals", path: "/proposals", label: "Proposals", icon: FileText, surface: "sales.proposals" },
      { key: "customers", path: "/clients", label: "Customers", icon: Building2, surface: "sales.customers" },
    ],
  },
  {
    label: "Operations",
    items: [
      { key: "ops-dashboard", path: "/ops", label: "Ops Dashboard", icon: Gauge, surface: "ops.dashboard" },
      { key: "work-orders", path: "/work-orders", label: "Work Orders", icon: ClipboardList, surface: "ops.work_orders" },
      { key: "purchasing", path: "/purchasing", label: "Purchasing", icon: ShoppingCart, surface: "ops.purchasing", surfaces: ["ops.purchasing", "ops.warehouse"] },
      { key: "invoicing", path: "/invoicing", label: "Invoicing", icon: Receipt, surface: "ops.invoices" },
      { key: "dispatch", path: "/dispatch", label: "Dispatch", title: "Dispatch Board", icon: CalendarClock, surface: "ops.dispatch" },
      { key: "timesheets", path: "/timesheets", label: "Timesheets", title: "Weekly Timesheets", icon: Clock3, surface: "ops.timesheets" },
      { key: "tech", path: "/field", label: "Tech", icon: Wrench, surface: "ops.tech" },
      { key: "map", path: "/map", label: "Map", icon: MapIcon, surface: "ops.map" },
    ],
  },
];

const COLLAPSE_KEY = "abs-sidebar-collapsed";
const GROUPS_KEY = "abs-sidebar-groups";

export const Sidebar = ({ mobileOpen = false, onNavigate, onOpenPalette }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, can } = useAuth();
  const { t } = useI18n();
  const [collapsed, setCollapsed] = useState(() => {
    try { return window.localStorage.getItem(COLLAPSE_KEY) === "1"; } catch { return false; }
  });
  // Collapsed nav groups, persisted by label.
  const [closedGroups, setClosedGroups] = useState(() => {
    try { return JSON.parse(window.localStorage.getItem(GROUPS_KEY) || "[]"); } catch { return []; }
  });
  const [theme, setTheme] = useState(() => resolveInitialTheme());
  useEffect(() => subscribeTheme(setTheme), []);

  useEffect(() => {
    try { window.localStorage.setItem(COLLAPSE_KEY, collapsed ? "1" : "0"); } catch { /* storage disabled */ }
  }, [collapsed]);
  useEffect(() => {
    try { window.localStorage.setItem(GROUPS_KEY, JSON.stringify(closedGroups)); } catch { /* storage disabled */ }
  }, [closedGroups]);

  const toggleGroup = (label) =>
    setClosedGroups(g => g.includes(label) ? g.filter(x => x !== label) : [...g, label]);

  // "nathan.fletemeyer@absinc.com" → "NF"
  const initials = (() => {
    const src = (user?.name || (user?.email || "").split("@")[0] || "").trim();
    const parts = src.split(/[\s._-]+/).filter(Boolean);
    return parts.slice(0, 2).map(p => p[0].toUpperCase()).join("") || "?";
  })();

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const rowBase = "group relative flex items-center rounded-md text-sm transition-colors duration-150";
  const rowPad = collapsed ? "justify-center !px-0 py-2" : "gap-3 px-3 py-2";
  const idleRow = "text-slate-400 font-medium hover:bg-white/[0.04] hover:text-slate-100";

  return (
    <aside
      className={`abs-sidebar fixed md:sticky top-0 left-0 z-40 h-screen shrink-0 flex flex-col
        transition-[width,transform] duration-200 ease-out
        ${mobileOpen ? "translate-x-0" : "-translate-x-full"} md:translate-x-0`}
      style={{ width: collapsed ? 64 : 188 }}
      data-testid="abs-sidebar"
      data-collapsed={collapsed ? "true" : "false"}
    >
      {/* Brand row — mark top-left · centered wordmark · collapse toggle. Same height as the page header (h-16)
          with the divider underneath so the two lines meet. Account lives in the footer. */}
      <div className={`flex items-center shrink-0 border-b h-16 ${collapsed ? "flex-col justify-center gap-1 px-2" : "px-3"}`} style={{ borderColor: "#262626" }} data-testid="sidebar-brand-row">
        {!collapsed && (
          <button
            type="button"
            onClick={() => { navigate(resolveLandingPath(user, can)); onNavigate?.(); }}
            title={t("Go to my landing page")}
            className="inline-flex flex-col items-stretch mx-auto leading-none hover:opacity-100 opacity-90 transition-opacity duration-150"
            data-testid="abs-logo-home"
          >
            <img
              src="/abs-logo.png"
              alt="ABS"
              className="h-8 w-auto"
              style={{ filter: "brightness(0) invert(1)" }}
              data-testid="abs-logo"
            />
            <span
              className="mt-[3px] w-full flex justify-between text-[8px] font-medium uppercase text-slate-400"
              data-testid="abs-logo-wordmark"
              aria-label="Service"
            >
              {"SERVICE".split("").map((c, i) => <span key={i}>{c}</span>)}
            </span>
          </button>
        )}

        <button
          type="button"
          onClick={() => setCollapsed(c => !c)}
          title={collapsed ? t("Expand sidebar") : t("Collapse sidebar")}
          aria-label={collapsed ? t("Expand sidebar") : t("Collapse sidebar")}
          className={`flex items-center justify-center ${collapsed ? "w-7 h-7" : "w-8 h-8"} shrink-0 rounded-md text-slate-500 hover:text-slate-100 hover:bg-white/[0.06] transition-colors duration-150`}
          data-testid="sidebar-collapse-toggle"
        >
          {collapsed ? <PanelLeftOpen className="w-[18px] h-[18px]" /> : <PanelLeftClose className="w-[18px] h-[18px]" />}
        </button>
      </div>

      {/* Nav groups — section labels are click-to-collapse */}
      <nav className="flex-1 overflow-y-auto overflow-x-hidden px-2 pt-2 pb-3 abs-sidebar-scroll" data-testid="top-nav">
        {NAV_GROUPS.map(group => {
          const items = group.items.filter(item => (item.surfaces || [item.surface]).some(s => can(s, "read")));
          if (!items.length) return null;
          const closed = !collapsed && closedGroups.includes(group.label);
          return (
            <div key={group.label} className="mb-3">
              {!collapsed && (
                <button
                  type="button"
                  onClick={() => toggleGroup(group.label)}
                  className="w-full flex items-center gap-1 px-3 pb-1.5 pt-2 text-[10px] font-semibold uppercase tracking-wider text-brand hover:text-sky-300 transition-colors duration-150"
                  title={closed ? t("Expand {group}", { group: t(group.label) }) : t("Collapse {group}", { group: t(group.label) })}
                  data-testid={`nav-group-${group.label.toLowerCase()}`}
                >
                  {closed
                    ? <ChevronRight className="w-3 h-3 shrink-0" />
                    : <ChevronDown className="w-3 h-3 shrink-0" />}
                  {t(group.label)}
                </button>
              )}
              {!closed && (
                <div className="space-y-0.5">
                  {items.map(item => {
                    const Icon = item.icon;
                    return (
                      <NavLink
                        key={item.path}
                        to={item.path}
                        end={item.path === "/"}
                        title={t(item.label)}
                        className={({ isActive }) =>
                          `${rowBase} ${rowPad} ${
                            isActive ? "bg-white/[0.06] text-white font-semibold" : idleRow
                          }`
                        }
                        data-testid={`nav-tab-${item.key}`}
                        onClick={(e) => {
                          const currentPath = location.pathname === "/" ? "/" : location.pathname.replace(/\/$/, "");
                          if (currentPath === item.path) {
                            e.preventDefault();
                            navigate(item.path, { state: { reset: Date.now() } });
                          }
                          onNavigate?.();
                        }}
                      >
                        {({ isActive }) => (
                          <>
                            {isActive && <span className="abs-nav-accent" aria-hidden="true" />}
                            <Icon className={`w-[18px] h-[18px] shrink-0 ${isActive ? "text-brand" : ""}`} />
                            {!collapsed && (
                              <span className="truncate" data-testid={`nav-${item.key}`}>{t(item.label)}</span>
                            )}
                          </>
                        )}
                      </NavLink>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* Footer — Light/Dark Mode · My Profile (account menu) · Settings. Quick Search lives in the page header. */}
      <div className="shrink-0 border-t px-2 py-1.5 space-y-0.5" style={{ borderColor: "#262626" }}>
        <button
          type="button"
          onClick={() => setTheme(toggleThemeUtil(theme))}
          title={theme === "dark" ? t("Switch to light mode") : t("Switch to dark mode")}
          aria-label={theme === "dark" ? t("Switch to light mode") : t("Switch to dark mode")}
          className={`${rowBase} w-full ${idleRow} ${rowPad}`}
          data-testid="theme-toggle-btn"
        >
          <span className="shrink-0" data-testid="theme-toggle">
            {theme === "dark" ? <Sun className="w-[18px] h-[18px]" /> : <Moon className="w-[18px] h-[18px]" />}
          </span>
          {!collapsed && <span className="truncate">{theme === "dark" ? t("Light Mode") : t("Dark Mode")}</span>}
        </button>

        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              title={user?.email || t("Account")}
              className={`${rowBase} w-full ${idleRow} ${rowPad}`}
              data-testid="sidebar-user-card"
              aria-label={t("Account Menu")}
            >
              <span className="w-[18px] h-[18px] shrink-0 rounded-full bg-white/[0.10] text-slate-200 text-[9px] font-semibold inline-flex items-center justify-center" data-testid="user-chip">
                {initials !== "?" ? initials : <UserIcon className="w-3 h-3" />}
              </span>
              {!collapsed && <span className="truncate">{t("My Profile")}</span>}
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-64 p-3" align="end" side="right">
            <div className="space-y-2">
              <div>
                <div className="text-sm font-semibold text-slate-900" data-testid="user-menu-name">{user?.name || user?.email}</div>
                <div className="text-xs text-slate-500" data-testid="user-menu-email">
                  <span data-testid="user-email">{user?.email}</span>
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                <Badge variant="outline" className="text-[10px] font-mono uppercase" data-testid="user-menu-role">{user?.role}</Badge>
              </div>
              <UserPreferences />
              <div className="pt-2 border-t border-slate-100 space-y-1">
                <Button
                  variant="ghost" size="sm"
                  className="w-full justify-start text-xs h-8"
                  onClick={() => navigate("/profile")}
                  data-testid="btn-my-profile"
                >
                  <UserIcon className="w-3.5 h-3.5 mr-2" />
                  {t("My profile & tickets")}
                </Button>
                <ConnectOutlookButton />
                <Button variant="outline" size="sm" className="w-full justify-start text-xs h-8" onClick={handleLogout} data-testid="btn-logout">
                  <LogOut className="w-3.5 h-3.5 mr-2" />
                  {t("Sign out")}
                </Button>
              </div>
            </div>
          </PopoverContent>
        </Popover>

        {["settings.general","settings.users","settings.dispatch","settings.equipment","settings.vendors","settings.inspections","settings.geocoding","settings.wiki"].some(s => can(s, "read")) && (
          <NavLink
            to="/settings"
            title={t("Settings")}
            className={({ isActive }) =>
              `${rowBase} ${rowPad} ${isActive ? "bg-white/[0.06] text-white font-semibold" : idleRow}`
            }
            data-testid="nav-tab-settings"
            onClick={() => onNavigate?.()}
          >
            {({ isActive }) => (
              <>
                {isActive && <span className="abs-nav-accent" aria-hidden="true" />}
                <span className="relative shrink-0" data-testid="nav-settings">
                  <Settings className={`w-[18px] h-[18px] ${isActive ? "text-brand" : ""}`} />
                </span>
                {!collapsed && <span className="truncate">{t("Settings")}</span>}
              </>
            )}
          </NavLink>
        )}
      </div>
    </aside>
  );
};

export default Sidebar;
